// jobHelp — the "AI assistant" the classic app has, honestly named.
//
// The last ❌ on docs/REBUILD_PARITY.md that was not blocked by a rule about
// money or permissions, and the only one that needed a decision rather than
// code. Three decisions were made here; each is written down because the next
// person to touch this will otherwise re-derive them.
//
// ── 1. It is NOT AI, so it is not called AI ─────────────────────────────────
//
// client/src/components/AIAssistant/AIAssistant.js is 238 lines and contains
// no model, no request, and no server route. It is:
//
//   · a text box that opens a Google search in a new tab
//   · six keyword-matched static tips, hardcoded in the file
//   · a photo button that links to Google Lens and Google Images
//
// A screen labelled "AI Job Assistant" doing keyword matching over six strings
// is this repo's own recurring defect in product form: a thing claiming more
// than it can support, which is trusted because of the claim. A crew member
// who believes there is a model behind it asks it a question it cannot answer
// and gets six canned paragraphs about paint. So the rebuild calls it JOB
// HELP, and says on the screen that the searches open Google.
//
// ── 2. No fourth Field tab ──────────────────────────────────────────────────
//
// REBUILD_SPEC §2 fixes Field at three tabs — Today, Hours, Me — and that is
// not a detail to trade away for a link-out. This is reached from the JOB
// screen, which is where it is used: somebody standing in a yard looking at a
// weed, a broken valve or a rotted sill. It is reachable from Me as well, for
// the case where nobody is on a job yet.
//
// ── 3. The TIPS are the part worth porting; the links are the part that is not
//
// Field is offline-first, and this is the split that matters there. A link-out
// needs signal. The tips do not — they are six strings in this file. So a crew
// member with no bars still gets the electrical safety warning, and the search
// buttons say they need signal instead of opening a tab onto nothing.
//
// That inverts the classic screen's emphasis, where the search is the feature
// and the tips are a garnish. On a phone in a yard it is the other way round.

/**
 * Match a keyword as a WORD rather than a substring.
 *
 * Needed for 'air' and ONLY for 'air'. This is carried over from the classic
 * file rather than rediscovered: 'repair', 'chair' and 'stairs' all contain
 * it, so "how to repair rotted wood on exterior" — which is one of that
 * screen's own suggested searches — answered with "Check/replace filters
 * monthly." The shipped example demonstrated the bug.
 *
 * The other keywords stay on `includes` deliberately: 'heat' has to keep
 * matching 'heater' and 'heating', and word-boundary matching would lose that.
 */
const hasWord = (text, word) => new RegExp(`\\b${word}\\b`).test(text);

/**
 * The six tips, and the keywords that reach each.
 *
 * `word: true` means match on a word boundary — see hasWord above. This is the
 * whole rule table, in one place, because the classic version had it spread
 * through six `if` statements and the 'air' bug lived in one of them.
 */
export const TIPS = [
  {
    id: 'paint',
    tone: 'info',
    title: 'Paint & colour',
    keywords: ['paint', 'color', 'colour'],
    text: 'For exterior paint, use 100% acrylic latex. For trim, semi-gloss works best. Always prime bare wood first. Coverage is typically 350-400 sq ft per gallon.',
  },
  {
    id: 'plumbing',
    tone: 'info',
    title: 'Plumbing',
    keywords: ['leak', 'water', 'plumb'],
    text: 'Turn off the water supply first. Check for visible pipe damage. For small leaks, pipe clamps or epoxy putty work as temporary fixes. For larger issues, photograph it and tell the office.',
  },
  {
    id: 'electrical',
    // A WARNING, not a tip. Somebody can be hurt by this one, and the classic
    // suite pins the distinction (AIAssistant.test.js, "marks electrical as a
    // warning and plumbing as a tip, not the reverse").
    tone: 'stop',
    title: 'Electrical safety',
    keywords: ['electric', 'outlet', 'wire'],
    text: 'Always turn off the breaker before working on electrical. If you are unsure, photograph it and ask for a licensed electrician. Never work on electrical in wet conditions.',
  },
  {
    id: 'mold',
    tone: 'stop',
    title: 'Mould safety',
    keywords: ['mold', 'mould', 'mildew'],
    text: 'Wear an N95 mask and gloves. For areas under 10 sq ft, clean with a water and detergent solution. For larger areas, ask for professional remediation. Photograph it either way.',
  },
  {
    id: 'hvac',
    tone: 'info',
    title: 'HVAC',
    // 'air' is the word-boundary one. Everything else here is a substring so
    // 'heat' still reaches 'heater' and 'heating'.
    keywords: ['hvac', 'heat', 'cool', { word: 'air' }],
    text: 'Check and replace filters monthly. Clear debris from outdoor units. Keep vents unobstructed. If a unit is not working, check the thermostat batteries and the breaker first.',
  },
  {
    id: 'lawn',
    tone: 'info',
    title: 'Lawn & landscaping',
    keywords: ['lawn', 'grass', 'landscape'],
    text: 'Mow at 3-3.5 inches. Water deeply once or twice a week. Edge along walkways for a clean finish. Apply pre-emergent in early spring.',
  },
];

/**
 * The tips a question reaches, in table order.
 *
 * Case-insensitive, and an empty or absent question reaches nothing — asking
 * for tips before anybody has typed would print six paragraphs at somebody who
 * has not asked a question.
 */
export function tipsFor(question) {
  const q = String(question || '').toLowerCase().trim();
  // No `if (!q) return []` here, and that is a decision the sabotage pass
  // made rather than an omission. Deleting one produced ZERO failures, because
  // an empty question matches nothing anyway: `''.includes('paint')` is false
  // and `/\bair\b/.test('')` is false. It was a guard that guarded nothing.
  //
  // What it LOOKED like it was protecting against is real, though, and is now
  // pinned where it belongs: an empty string in the keyword table would make
  // `''.includes('')` true and every tip match everything. jobHelp.test.js
  // asserts no keyword is empty, so the behaviour above falls out of the table
  // being well-formed instead of out of a line nothing reaches.
  return TIPS.filter((t) => t.keywords.some((k) => (
    typeof k === 'string' ? q.includes(k) : hasWord(q, k.word)
  )));
}

/** The suggested searches, as the classic screen offers them. */
export const QUICK = [
  { label: 'Paint coverage', q: 'how to calculate paint coverage for walls' },
  { label: 'Leaky faucet', q: 'how to fix a leaky faucet step by step' },
  { label: 'Identify a weed', q: 'identify common weeds in lawn' },
  { label: 'Reset a breaker', q: 'how to safely reset a tripped circuit breaker' },
  { label: 'Wood rot', q: 'how to repair rotted wood on exterior' },
  { label: 'Cleaning checklist', q: 'professional property cleaning tips and checklist' },
];

/**
 * Where a question goes, or `null` when there is nothing to ask.
 *
 * `null` rather than a Google search for an empty string, which would open a
 * blank results page and read as the button being broken.
 */
export function searchLinks(question) {
  const q = String(question || '').trim();
  if (!q) return null;
  const e = encodeURIComponent(q);
  return {
    query: q,
    google: `https://www.google.com/search?q=${e}`,
    youtube: `https://www.youtube.com/results?search_query=${e}`,
  };
}

/** The two tools that can actually read a photo. Neither needs a question. */
export const PHOTO_LINKS = [
  { label: 'Google Lens', url: 'https://lens.google.com/', why: 'identify a plant, a part or a problem from a photo' },
  { label: 'Google Images', url: 'https://images.google.com/', why: 'search by picture' },
];

/**
 * What the screen may offer right now.
 *
 * OFFLINE IS THE POINT. Every link here leaves the app, so with no signal a
 * search button opens a tab onto a connection error — which reads as the app
 * being broken rather than as the phone having no bars. The tips do not need
 * signal, so offline keeps them and says why the rest is unavailable.
 */
export function helpState({ question, online = true } = {}) {
  const tips = tipsFor(question);
  const links = searchLinks(question);
  return {
    tips,
    links: online ? links : null,
    photo: online ? PHOTO_LINKS : [],
    // A sentence rather than a disabled button with no reason. "We do not
    // know" and "there is nothing" are different, and so are "no signal" and
    // "no answer".
    offlineNote: online ? null
      : 'These searches open Google, so they need a signal. The safety notes above do not.',
    // Asked something, matched nothing, and online: the honest answer is that
    // this holds six notes, not that the question was bad.
    noTipNote: question && tips.length === 0
      ? 'Nothing here matches that. This holds six safety and method notes — the search below goes to Google for anything else.'
      : null,
  };
}
